import React, { useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocationParams } from '../../hooks/useLocationParams';
import { PageBanner } from '../../components/PageBanner/PageBanner';
import { PageNavigation, Tab } from '../../components/PageNavigation/PageNavigation';
import { EventCard } from '../../components/EventCard/EventCard';
import { CityFilter } from '../../components/CityFilter/CityFilter';
import ShowroomTabs, { ShowroomTab } from '../../components/ShowroomTabs/ShowroomTabs';
import { useEvents } from '../../hooks/useEvents';
import { adaptEventToCardEvent } from '../../adapters/eventAdapter';
import { useEventBooking } from '../../hooks/useEventBooking';
import languageIcon from '../../assets/icons/language-icon-white.svg';

import './EventsPage.scss';

// Feature flag: Set to true to show events, false to show "Coming Soon"
const SHOW_EVENTS = import.meta.env.VITE_MICROSITE_SHOW_EVENTS === "true";

const EventsPage: React.FC = () => {
  const { t, i18n } = useTranslation('events');
  const [activeTab, setActiveTab] = useState('events');
  const { location: initialLocation, testingParams, updateParams } = useLocationParams();
  const [selectedCity, setSelectedCity] = useState(initialLocation);
  
  // Only fetch events if the feature is enabled OR if in testing mode
  const { data: events = [], isLoading: isEventsLoading } = (SHOW_EVENTS || testingParams.isTesting)
    ? useEvents(selectedCity)
    : { data: [], isLoading: false };
  const { isPending: isBookingPending, isSuccess: isBookingSuccess } = useEventBooking();
  const [selectedShowroom, setSelectedShowroom] = useState<ShowroomTab>('all');
  const navigate = useNavigate();

  if (isEventsLoading) {
    return <Loader variant="fullscreen" />;
  }

  const handleTabChange = (tabId: string) => {
    if (tabId === 'booking') {
      navigate('/appointment');
    } else {
      setActiveTab(tabId);
    }
  };

  const eventsTabs: Tab[] = [
    { id: 'events', label: t('navigation.events') },
    { id: 'booking', label: t('navigation.booking') }
  ];

  return (
    <div className="events-page">
      <button 
        className="language-toggle"
        onClick={() => {
          const newLang = i18n.language === 'en' ? 'fr' : 'en';
          i18n.changeLanguage(newLang);
          updateParams({ lang: newLang });
        }}
      >
        <img src={languageIcon} alt="Change Language" />
        <span>{i18n.language.toUpperCase()}</span>
      </button>
      <PageBanner
        title={t('banner.title')}
        subtitle={t('banner.subtitle')}
        className="events-page__banner"
      />
      <div className="events-page__content">
        <PageNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={eventsTabs}
        />
        <div className="events-page__inner-content">
          {activeTab === 'events' ? (
            <div className="events-page__events-content">
              <div className="filters-container">
                <CityFilter
                  cities={['Montreal', 'Toronto']}
                  selectedCity={selectedCity}
                  onCityChange={(city) => {
                    setSelectedCity(city);
                    setSelectedShowroom('all');
                    updateParams({ location: city });
                  }}
                />
                {selectedCity === 'Montreal' && (
                  <ShowroomTabs
                    selectedTab={selectedShowroom}
                    onTabChange={setSelectedShowroom}
                    tabs={[
                      { id: 'all', label: t('showroom.tabs.all'), count: events.filter(e => e.city === selectedCity).length },
                      { id: 'Montreal Showroom', label: t('showroom.montreal'), count: events.filter(e => e.city === selectedCity && e.showroom?.toUpperCase().includes('MONTREAL SHOWROOM')).length },
                      { id: 'RoyalMount Showroom', label: t('showroom.royalmount'), count: events.filter(e => e.city === selectedCity && e.showroom?.toUpperCase().includes('ROYALMOUNT SHOWROOM')).length }
                    ]}
                  />
                )}
              </div>
              <div className="events-page__events-grid">
                {(() => {
                  const filteredEvents = events.filter(event => {
                    // First filter by city
                    if (event.city !== selectedCity) return false;
                    // Then filter by showroom if in Montreal
                    if (selectedCity === 'Montreal' && selectedShowroom !== 'all') {
                      if (selectedShowroom === 'Montreal Showroom') {
                        return event.showroom?.toUpperCase().includes('MONTREAL SHOWROOM');
                      } else if (selectedShowroom === 'RoyalMount Showroom') {
                        return event.showroom?.toUpperCase().includes('ROYALMOUNT SHOWROOM');
                      }
                      return false;
                    }
                    return true;
                  });

                  return filteredEvents.length === 0 ? (
                    <div className="events-page__no-events">
                      <p>{t('noEvents')}</p>
                    </div>
                  ) : (
                    filteredEvents.map((event, index) => {
                      // Create a unique key using appointmentId or fallback to index
                      const uniqueKey = event.appointmentId || event.id || `event-${index}`;
                      return (
                        <EventCard
                          key={uniqueKey}
                          event={adaptEventToCardEvent(event)}
                          formVariant="event"
                        />
                      );
                    })
                  );
                })()} 
              </div>
            </div>
          ) : (
            <div className="events-page__booking-content">
              <div className="events-page__booking-instructions">
                <h3>{t('booking.title')}</h3>
                <p>{t('booking.description')}</p>
              </div>
              <div className="events-page__events-grid">
                {(() => {
                  const filteredEvents = events.filter(event => {
                    // First filter by city
                    if (event.city !== selectedCity) return false;
                    // Then filter by showroom if in Montreal
                    if (selectedCity === 'Montreal' && selectedShowroom !== 'all') {
                      if (selectedShowroom === 'Montreal Showroom') {
                        return event.showroom?.toUpperCase().includes('MONTREAL SHOWROOM');
                      } else if (selectedShowroom === 'RoyalMount Showroom') {
                        return event.showroom?.toUpperCase().includes('ROYALMOUNT SHOWROOM');
                      }
                      return false;
                    }
                    return true;
                  });

                  return filteredEvents.length === 0 ? (
                    <div className="events-page__no-events">
                      <p>{t('noEvents')}</p>
                    </div>
                  ) : (
                    filteredEvents.map((event, index) => {
                      // Create a unique key using appointmentId or fallback to index
                      const uniqueKey = event.appointmentId || event.id || `event-${index}`;
                      return (
                        <EventCard
                          key={uniqueKey}
                          event={adaptEventToCardEvent(event)}
                          formVariant="event"
                        />
                      );
                    })
                  );
                })()} 
              </div>
            </div>
          )}
        </div>
      </div>
      {isBookingSuccess && (
        <div className="events-page__booking-success">
          <div className="events-page__booking-success-content">
            <h3>{t('booking.success.title')}</h3>
            <p>{t('booking.success.message')}</p>
            <button 
              className="events-page__booking-success-close" 
              onClick={() => window.location.reload()}
            >
              {t('booking.success.close')}
            </button>
          </div>
        </div>
      )}
      {isBookingPending && (
        <div className="events-page__booking-loading">
          <Loader variant="overlay" />
          <p>{t('booking.loading')}</p>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
